/**
 * Shared helpers for the Home crew widgets (My Crew — Live Status + Crew Map).
 * Ported from the design prototype
 * docs/featureBriefs/design_handoff_home_crew/Home - Hi-Fi.html (hueFor,
 * initials, status meta, site grouping) and wired to the CrewLiveStatus API
 * shapes. Time display reuses the existing offset-aware helpers in
 * util/DateAndTimeUtil so "since"/"last out" render in the punch's OWN offset.
 */

import { applyOffsetToUTC, convertToLocalTime } from '../../util/DateAndTimeUtil';
import { CrewMemberStatus, CrewMemberStatusKey, CrewPunchSegment } from '../../interfaces/CrewLiveStatus';

/** Deterministic hue from a name — port of hueFor() in the prototype HTML. */
export function hueFor(s: string): number {
  let h = 7;
  for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) % 360;
  return h;
}

/** oklch avatar background per the design tokens (§Avatar color). */
export function avatarColor(name: string): string {
  return `oklch(0.58 0.13 ${hueFor(name)})`;
}

/** "Scott Nalder" → "SN" — port of initials() in the prototype HTML. */
export function initials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase();
}

export function fullName(m: CrewMemberStatus): string {
  return `${m.name.first} ${m.name.last}`.replace(/\s+/g, ' ').trim();
}

/**
 * Display status: splits the API's 'out' into "Clocked out" (had punches today)
 * vs "No work" (no punches today) — derived client-side from punches[].
 */
export type CrewDisplayStatusKey = CrewMemberStatusKey | 'none';

export function displayStatus(m: CrewMemberStatus): CrewDisplayStatusKey {
  if (m.status !== 'out') return m.status;
  return m.punches.length > 0 ? 'out' : 'none';
}

/** Status display meta (label + pill/dot class suffixes) per the prototype STAT map. */
export const CREW_STAT: Record<CrewDisplayStatusKey, { label: string; pill: string; dot: string }> = {
  in: { label: 'On the clock', pill: 'st-in', dot: 'dot-in' },
  meal: { label: 'Meal break', pill: 'st-meal', dot: 'dot-meal' },
  out: { label: 'Clocked out', pill: 'st-out', dot: 'dot-out' },
  none: { label: 'No work', pill: 'st-none', dot: 'dot-out' },
};

export function wageTypeLabel(wageType: CrewMemberStatus['wageType']): string {
  if (wageType === 'hourly') return 'Hourly';
  if (wageType === 'piece') return 'Piece';
  return '';
}

/**
 * Mono cost-code line: "{name} · {code}"; NPM hourly-job punches carry a
 * projectPhase instead of an accountCode; piece punches are uncoded.
 */
export function costCodeText(m: CrewMemberStatus): string {
  if (m.accountCode) return `${m.accountCode.name} · ${m.accountCode.altAccountCodeID}`;
  if (m.projectPhase) return `${m.projectPhase.altProjectPhaseID} · ${m.projectPhase.name}`;
  if (m.wageType === 'piece') return 'Piece work';
  if (m.kioskUser && m.punchSource === 'timeEntry') return 'Warehouse';
  return '—';
}

/**
 * NPM free-text service order (legacy TEH ServiceOrderText): serviceOrder
 * present with a null altServiceOrderID. WCDP/2020ph always emit a non-null
 * altServiceOrderID (real SO # or temp description), so this is true only for
 * NPM's free-text punches.
 */
export function isFreeTextSO(
  so: { altServiceOrderID: string | null; name: string | null } | null | undefined,
): boolean {
  return so != null && so.altServiceOrderID == null;
}

/** "6:58 AM" in the punch's own offset (existing DateAndTimeUtil helper). */
export function formatPunchTime(dateTime: string, offset: string | null): string {
  return applyOffsetToUTC(dateTime, offset || '');
}

/** Elapsed since a UTC instant — "4h 07m" (zero-padded mins) or "22m" under an hour. */
export function formatElapsed(sinceISO: string, nowMs: number): string {
  const start = new Date(sinceISO).getTime();
  if (Number.isNaN(start)) return '';
  const mins = Math.max(0, Math.floor((nowMs - start) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}

/** Is the instant "today" evaluated in the punch's own offset? */
export function isTodayInOffset(dateTime: string, offset: string | null, nowMs: number): boolean {
  const d = new Date(dateTime);
  if (Number.isNaN(d.getTime())) return false;
  const off = offset || '+00:00';
  const local = convertToLocalTime(dateTime, off);
  const localNow = convertToLocalTime(new Date(nowMs).toISOString(), off);
  return (
    local.getUTCFullYear() === localNow.getUTCFullYear() &&
    local.getUTCMonth() === localNow.getUTCMonth() &&
    local.getUTCDate() === localNow.getUTCDate()
  );
}

/** Italic job-cell line for clocked-out rows. */
export function clockedOutText(m: CrewMemberStatus, nowMs: number): string {
  if (m.lastOutDateTime && isTodayInOffset(m.lastOutDateTime, m.lastOutDateTimeOffset, nowMs)) {
    return `Last out ${formatPunchTime(m.lastOutDateTime, m.lastOutDateTimeOffset)}`;
  }
  return 'Not clocked in today';
}

// ── Location-based employee helpers ──────────────────────────────────────────

/**
 * Returns the location mode for an on-clock employee with no project:
 * - 'csw'     when customerServiceWarranty is present (WCDP CSW punch)
 * - 'warehouse' when punchSource === 'timeEntry' AND the role is a kiosk role
 *               (NPM warehouse / driver — hourly, clocked at the kiosk; TAS-541)
 * - 'piece'   when punchSource === 'timeEntry' (WCDP raw-piece punch)
 * - 'service' when a serviceOrder is present
 * - 'hourly'  when on a non-job hourly punch (accountCode, no project)
 * - null      for any other state (job-based, or clocked out)
 */
export type CrewLocMode = 'csw' | 'warehouse' | 'piece' | 'service' | 'hourly';

export function locMode(m: CrewMemberStatus): CrewLocMode | null {
  if (m.status === 'out' || m.project !== null) return null;
  if (m.customerServiceWarranty != null) return 'csw';
  // Role decides, not the punch: a kiosk-role raw punch is hourly warehouse time.
  if (m.punchSource === 'timeEntry') return m.kioskUser ? 'warehouse' : 'piece';
  if (m.serviceOrder !== null && m.serviceOrder !== undefined) return 'service';
  if (m.accountCode !== null && m.accountCode !== undefined) return 'hourly';
  return null;
}

/**
 * Display label for a location-based employee's job cell and map pin.
 * csw     → locationName || csw.name || 'Service'
 * warehouse → 'Warehouse'
 * piece   → 'Piece work'
 * service → "SO {altServiceOrderID}"; NPM free-text SOs have no id and label
 *           as "Service Order" (the text renders as the detail line)
 * hourly  → "Hourly (non job-time)"
 * Returns null for job-based or clocked-out employees.
 */
export function locLabel(m: CrewMemberStatus): string | null {
  const mode = locMode(m);
  if (mode === 'csw') {
    const csw = m.customerServiceWarranty!;
    return csw.locationName || csw.name || 'Service';
  }
  if (mode === 'warehouse') return 'Warehouse';
  if (mode === 'piece') return 'Piece work';
  if (mode === 'service') {
    const altID = m.serviceOrder?.altServiceOrderID;
    return altID ? `SO ${altID}` : 'Service Order';
  }
  if (mode === 'hourly') return 'Hourly (non job-time)';
  return null;
}

/**
 * Returns only the on-clock (in/meal) employees that have NO project —
 * i.e. the location-based subset shown as individual loc-pins on the map.
 */
export function locEmployees(crew: CrewMemberStatus[]): CrewMemberStatus[] {
  return crew.filter((m) => locMode(m) !== null);
}

/** A job site = client-side grouping of in/meal employees by project.projectID. */
export interface CrewSite {
  projectID: number;
  altProjectID: string;
  name: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  /** Employees currently in/meal at this site (crew order preserved). */
  employees: CrewMemberStatus[];
  /** Amber pin when anyone at the site is on a meal break. */
  anyMeal: boolean;
}

export function groupSites(crew: CrewMemberStatus[]): CrewSite[] {
  const byID = new Map<number, CrewSite>();
  crew.forEach((m) => {
    if (m.status === 'out' || !m.project) return;
    let site = byID.get(m.project.projectID);
    if (!site) {
      site = {
        projectID: m.project.projectID,
        altProjectID: m.project.altProjectID,
        name: m.project.name,
        address: m.project.address,
        latitude: m.project.latitude,
        longitude: m.project.longitude,
        employees: [],
        anyMeal: false,
      };
      byID.set(m.project.projectID, site);
    }
    site.employees.push(m);
    if (m.status === 'meal') site.anyMeal = true;
  });
  return Array.from(byID.values());
}

export function siteHasCoords(site: CrewSite): site is CrewSite & { latitude: number; longitude: number } {
  return (
    typeof site.latitude === 'number' && !Number.isNaN(site.latitude) &&
    typeof site.longitude === 'number' && !Number.isNaN(site.longitude)
  );
}

/**
 * Map position for an employee marker: their actual punch GPS, falling back to
 * a small ring around the site coordinate when gps is null (per the handoff).
 * `ringIndex`/`ringCount` place the fallback markers evenly around the site.
 */
export function employeeMarkerPos(
  m: CrewMemberStatus,
  site: CrewSite,
  ringIndex: number,
  ringCount: number,
): [number, number] | null {
  if (m.gps) return [m.gps.latitude, m.gps.longitude];
  if (!siteHasCoords(site)) return null;
  if (ringCount <= 1) return [site.latitude + 0.0003, site.longitude];
  const angle = -Math.PI / 2 + (ringIndex / ringCount) * Math.PI * 2;
  const r = 0.0004; // ~45m ring
  return [site.latitude + Math.sin(angle) * r * 0.85, site.longitude + Math.cos(angle) * r];
}

/** HTML-escape for strings interpolated into Leaflet divIcon html. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Total worked minutes today (non-meal segments; open regular segment
 * counted to nowMs). Returns 0 when no punches.
 */
export function totalWorkedMinutes(
  punches: CrewPunchSegment[],
  status: CrewMemberStatusKey,
  nowMs: number,
): number {
  if (!punches.length) return 0;
  let total = 0;
  for (const seg of punches) {
    if (seg.type === 2) continue; // skip meal segments
    const startMs = new Date(seg.inDateTime).getTime();
    const endMs = seg.outDateTime === null ? nowMs : new Date(seg.outDateTime).getTime();
    total += Math.max(0, endMs - startMs);
  }
  return Math.round(total / 60000);
}

/** Format minutes as "4h 07m" or "22m". */
export function formatDuration(minutes: number): string {
  if (minutes <= 0) return '0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}

/**
 * Status-cell time line for anyone with punches today (on the clock, at meal,
 * or already clocked out): "since {first punch-in of the day}" plus the
 * running total WORK time for the day (meals excluded; open segment counts
 * to now). Null when the employee has no punches today.
 */
export function workSummary(
  m: CrewMemberStatus,
  nowMs: number,
): { since: string; worked: string } | null {
  if (!m.punches || m.punches.length === 0) return null;
  const first = m.punches[0];
  return {
    since: formatPunchTime(first.inDateTime, first.inDateTimeOffset),
    worked: formatDuration(totalWorkedMinutes(m.punches, m.status, nowMs)),
  };
}
